import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  CalendarDays,
  AlertTriangle,
  Wallet,
  BarChart2,
  BellRing,
  CreditCard,
  Trophy,
  Building2
} from 'lucide-react';

interface NavSection {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface DashboardNavigationProps {
  sections: NavSection[];
}

const NavWrapper = styled.div<{ $isSticky: boolean }>`
  position: ${props => props.$isSticky ? 'sticky' : 'relative'};
  top: ${props => props.$isSticky ? '0' : 'auto'};
  z-index: 100;
  background: ${props => props.$isSticky ? 'rgba(255, 255, 255, 0.95)' : 'transparent'};
  backdrop-filter: ${props => props.$isSticky ? 'blur(10px)' : 'none'};
  margin: 0 -32px;
  padding: 0 32px;
  transition: all 0.3s ease;

  ${props => props.$isSticky && `
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-bottom: 1px solid var(--border-color);
  `}

  @media (max-width: 768px) {
    margin: 0 -16px;
    padding: 0 16px;
  }
`;

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 0;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 4px;
    padding: 10px 0;
  }
`;

const NavItem = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.$isActive ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.$isActive ? 'white' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$isActive ? 'var(--primary-color)' : 'transparent'};
  border-radius: 25px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.$isActive ? 'var(--primary-hover)' : 'var(--background-color)'};
    color: ${props => props.$isActive ? 'white' : 'var(--primary-color)'};
    border-color: ${props => props.$isActive ? 'var(--primary-hover)' : 'var(--border-color)'};
  }

  svg {
    flex-shrink: 0;
    opacity: ${props => props.$isActive ? '1' : '0.7'};
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 12px;
    gap: 6px;
  }
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 32px;
  right: 32px;
  height: 2px;
  background: var(--border-color);
  border-radius: 1px;
  overflow: hidden;

  @media (max-width: 768px) {
    left: 16px;
    right: 16px;
  }
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${props => props.$progress}%;
  background: linear-gradient(90deg, var(--primary-color), #6366f1);
  border-radius: 1px;
  transition: width 0.15s ease-out;
`;

const DashboardNavigation: React.FC<DashboardNavigationProps> = ({ sections }) => {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || '');
  const [isSticky, setIsSticky] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      // Handle sticky state
      const scrollTop = window.scrollY;
      setIsSticky(scrollTop > 200);

      // Calculate scroll progress
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / documentHeight) * 100;
      setScrollProgress(Math.min(progress, 100));

      // Determine active section based on scroll position
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id)
      })).filter(s => s.element);

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80; // Account for sticky nav height
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  if (sections.length === 0) return null;

  return (
    <NavWrapper $isSticky={isSticky}>
      <NavContainer>
        {sections.map((section) => (
          <NavItem
            key={section.id}
            $isActive={activeSection === section.id}
            onClick={() => scrollToSection(section.id)}
          >
            {section.icon}
            {section.label}
          </NavItem>
        ))}
      </NavContainer>
      {isSticky && <ProgressBar><ProgressFill $progress={scrollProgress} /></ProgressBar>}
    </NavWrapper>
  );
};

// Pre-defined sections for System Admin dashboard (consolidated)
export const getAdminDashboardSections = (): NavSection[] => [
  { id: 'calendar', label: 'יומן', icon: <CalendarDays size={16} /> },
  { id: 'reminders', label: 'תזכורות', icon: <BellRing size={16} /> },
  { id: 'top-performers', label: 'מובילים', icon: <Trophy size={16} /> },
  { id: 'alerts', label: 'התראות', icon: <AlertTriangle size={16} /> },
  { id: 'financial', label: 'כספים', icon: <Wallet size={16} /> },
  { id: 'status-distribution', label: 'ביצועים', icon: <BarChart2 size={16} /> },
  { id: 'special-payments', label: 'תשלומים', icon: <CreditCard size={16} /> },
  { id: 'departments', label: 'מחלקות', icon: <Building2 size={16} /> },
];

export default DashboardNavigation;
